<script lang="ts">
	import { Dropdown } from "flowbite-svelte-v1";
	import Icon from "#lib/components/Icon.svelte";
	import { hsvToRgb, hsvaToHex, parseToHsva, rgbToHsv, rgbaToHex, type Hsva } from "#lib/components/flow/editor/appearance/colorMath.js";
	import { browser } from "$app/env";

	interface Props {
		id: string;
		value: string;
		disabled?: boolean;
		allowTransparent?: boolean;
		oninput: (value: string) => void;
		onchange: (value: string) => void;
	}

	let { id, value, disabled = false, allowTransparent = false, oninput, onchange }: Props = $props();

	function clamp(n: number, min: number, max: number): number {
		return Math.min(max, Math.max(min, n));
	}

	let open = $state(false);
	let hsva = $state<Hsva>({ h: 0, s: 0, v: 0, a: 255 });
	let svBox = $state<HTMLElement>();
	// Remembers our own last emission. The effect below parses again only when the value changes
	// from outside, like a reset or a brand kit pick, so a drag in progress is never clobbered.
	let lastEmitted: string | undefined;
	let dragging = false;

	// This runs before the first paint so the picker shows the incoming value right away.
	$effect.pre(() => {
		if (value !== lastEmitted) hsva = parseToHsva(value);
	});

	let swatchColor = $derived(hsvaToHex(hsva));
	let opaqueColor = $derived(rgbaToHex({ ...hsvToRgb(hsva), a: 255 }));
	let rgb = $derived(hsvToRgb(hsva));
	let alphaPercent = $derived(Math.round((hsva.a / 255) * 100));

	// The number fields recompute HSV from the edited channel and keep the current opacity.
	function setChannel(channel: "r" | "g" | "b", raw: string, kind: "input" | "change") {
		const next = { ...rgb, [channel]: clamp(Math.round(Number(raw) || 0), 0, 255) };
		hsva = { ...rgbToHsv(next), a: hsva.a };
		emit(kind);
	}

	function setAlpha(rawPct: string, kind: "input" | "change") {
		const pct = clamp(Number(rawPct) || 0, 0, 100);
		hsva.a = Math.round((pct / 100) * 255);
		emit(kind);
	}

	// EyeDropper is only in Chromium. The button stays hidden when the API is missing.
	const supportsEyeDropper = browser && "EyeDropper" in window;

	async function pickFromScreen() {
		if (!browser || !window.EyeDropper) return;
		let picked: string;
		try {
			({ sRGBHex: picked } = await new window.EyeDropper().open());
		} catch {
			// The user pressed Escape to dismiss the eyedropper. Leave the color as it was.
			return;
		}
		// Emitting outside the catch keeps a consumer's error visible instead of reading as a dismissal.
		hsva = { ...parseToHsva(picked), a: hsva.a }; // keep the current opacity
		emit("change");
	}

	function emit(kind: "input" | "change") {
		const hex = hsvaToHex(hsva);
		lastEmitted = hex;
		oninput?.(hex);
		if (kind === "change") onchange?.(hex);
	}

	function moveSv(event: PointerEvent) {
		if (!svBox) return;
		const rect = svBox.getBoundingClientRect();
		let s = clamp((event.clientX - rect.left) / rect.width, 0, 1) * 100;
		let v = (1 - clamp((event.clientY - rect.top) / rect.height, 0, 1)) * 100;
		// Snap the last percent to the extremes so pure white, pure black, and fully saturated colors are
		// reachable by dragging into a corner instead of landing one pixel short.
		if (s < 1) s = 0;
		else if (s > 99) s = 100;
		if (v < 1) v = 0;
		else if (v > 99) v = 100;
		hsva.s = s;
		hsva.v = v;
		emit("input");
	}

	function startSv(event: PointerEvent) {
		if (disabled) return;
		dragging = true;
		moveSv(event);
		window.addEventListener("pointermove", moveSv);
		window.addEventListener("pointerup", endSv);
	}

	function endSv() {
		if (!dragging) return;
		dragging = false;
		window.removeEventListener("pointermove", moveSv);
		window.removeEventListener("pointerup", endSv);
		emit("change");
	}

	function svKey(event: KeyboardEvent) {
		const step = event.shiftKey ? 10 : 2;
		switch (event.key) {
			case "ArrowRight":
				hsva.s = clamp(hsva.s + step, 0, 100);
				break;
			case "ArrowLeft":
				hsva.s = clamp(hsva.s - step, 0, 100);
				break;
			case "ArrowUp":
				hsva.v = clamp(hsva.v + step, 0, 100);
				break;
			case "ArrowDown":
				hsva.v = clamp(hsva.v - step, 0, 100);
				break;
			default:
				return;
		}
		event.preventDefault();
		emit("change");
	}
</script>

<button type="button" {id} {disabled} aria-label="Choose color" class="checker absolute inset-0 h-full w-full rounded-lg">
	<span class="block h-full w-full rounded-lg" style="background-color: {swatchColor};"></span>
</button>
<Dropdown simple bind:isOpen={open} placement="bottom-start" triggerDelay={0} transitionParams={{ duration: 0 }} class="z-50 w-64 rounded-lg p-3 normal-case shadow">
	<div
		bind:this={svBox}
		role="slider"
		tabindex="0"
		aria-label="Saturation and brightness"
		aria-valuemin={0}
		aria-valuemax={100}
		aria-valuenow={Math.round(hsva.s)}
		onpointerdown={startSv}
		onkeydown={svKey}
		class="relative mb-3 h-40 w-full cursor-crosshair rounded"
		style="background-color: hsl({hsva.h}, 100%, 50%); background-image: linear-gradient(to bottom, transparent, #000), linear-gradient(to right, #fff, transparent);"
	>
		<div class="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow" style="left: {hsva.s}%; top: {100 - hsva.v}%;"></div>
	</div>
	<div class="mb-2 flex items-center gap-2">
		{#if supportsEyeDropper}
			<button type="button" onclick={pickFromScreen} aria-label="Pick color from screen" class="shrink-0 p-0.5 text-gray-600 hover:text-gray-900">
				<Icon name="eyedropper-variant" iconSet="mdi" class="h-5.5 w-5.5" />
			</button>
		{/if}
		<div class="checker h-7 w-7 shrink-0 rounded-full">
			<div class="h-full w-full rounded-full" style="background-color: {swatchColor};"></div>
		</div>
		<input type="range" aria-label="Hue" class="picker-slider flex-1" min="0" max="360" bind:value={hsva.h} oninput={() => emit("input")} onchange={() => emit("change")} style="background: linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00);" />
	</div>
	{#if allowTransparent}
		<input type="range" aria-label="Opacity" class="picker-slider opacity-slider mb-3 w-full" min="0" max="100" value={alphaPercent} oninput={event => setAlpha(event.currentTarget.value, "input")} onchange={event => setAlpha(event.currentTarget.value, "change")} style="--track-gradient: linear-gradient(to right, transparent, {opaqueColor});" />
	{/if}
	<div class="flex items-end gap-2">
		<label class="flex flex-1 flex-col items-center gap-1">
			<input type="number" min="0" max="255" value={rgb.r} oninput={event => setChannel("r", event.currentTarget.value, "input")} onchange={event => setChannel("r", event.currentTarget.value, "change")} class="w-full rounded border border-gray-300 px-1 py-1 text-center text-xs" />
			<span class="text-xs text-gray-500">R</span>
		</label>
		<label class="flex flex-1 flex-col items-center gap-1">
			<input type="number" min="0" max="255" value={rgb.g} oninput={event => setChannel("g", event.currentTarget.value, "input")} onchange={event => setChannel("g", event.currentTarget.value, "change")} class="w-full rounded border border-gray-300 px-1 py-1 text-center text-xs" />
			<span class="text-xs text-gray-500">G</span>
		</label>
		<label class="flex flex-1 flex-col items-center gap-1">
			<input type="number" min="0" max="255" value={rgb.b} oninput={event => setChannel("b", event.currentTarget.value, "input")} onchange={event => setChannel("b", event.currentTarget.value, "change")} class="w-full rounded border border-gray-300 px-1 py-1 text-center text-xs" />
			<span class="text-xs text-gray-500">B</span>
		</label>
		{#if allowTransparent}
			<label class="flex flex-1 flex-col items-center gap-1">
				<input type="number" min="0" max="100" value={alphaPercent} oninput={event => setAlpha(event.currentTarget.value, "input")} onchange={event => setAlpha(event.currentTarget.value, "change")} class="w-full rounded border border-gray-300 px-1 py-1 text-center text-xs" />
				<span class="text-xs text-gray-500">A</span>
			</label>
		{/if}
	</div>
</Dropdown>

<style>
	@reference "#lib/assets/app.css";

	.checker {
		background-image: linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%);
		background-size: 12px 12px;
		background-position:
			0 0,
			0 6px,
			6px -6px,
			-6px 0;
	}

	/* Clean numeric fields like the native picker. No spin buttons. */
	input[type="number"] {
		appearance: textfield;
		-moz-appearance: textfield;
	}
	input[type="number"]::-webkit-inner-spin-button,
	input[type="number"]::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	/* Hue and opacity sliders. The gradient set in the inline style shows as the track because the
	   runnable track is transparent. The thumb is a draggable white circle like the native picker. */
	.picker-slider {
		appearance: none;
		-webkit-appearance: none;
		height: 14px;
		border-radius: 9999px;
		border: 1px solid rgb(0 0 0 / 0.1);
		cursor: pointer;
		outline: none;
	}
	.picker-slider::-webkit-slider-runnable-track {
		-webkit-appearance: none;
		height: 14px;
		background: transparent;
		border-radius: 9999px;
	}
	.picker-slider::-webkit-slider-thumb {
		-webkit-appearance: none;
		height: 16px;
		width: 16px;
		margin-top: -1px;
		border-radius: 9999px;
		background: #fff;
		border: 1px solid rgb(0 0 0 / 0.25);
		box-shadow: 0 1px 2px rgb(0 0 0 / 0.3);
	}
	.picker-slider::-moz-range-track {
		height: 14px;
		background: transparent;
		border-radius: 9999px;
	}
	.picker-slider::-moz-range-thumb {
		height: 16px;
		width: 16px;
		border-radius: 9999px;
		background: #fff;
		border: 1px solid rgb(0 0 0 / 0.25);
		box-shadow: 0 1px 2px rgb(0 0 0 / 0.3);
	}

	/* Opacity track. The gradient runs from transparent to the color over a checkerboard. Both live
	   in the track background so the checker matches the track height and never overflows. */
	.opacity-slider {
		background-color: #fff;
		background-image: var(--track-gradient), linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%);
		background-size:
			100% 100%,
			10px 10px,
			10px 10px,
			10px 10px,
			10px 10px;
		background-position:
			0 0,
			0 0,
			0 5px,
			5px -5px,
			-5px 0;
	}
</style>
